import React, { useEffect, useState } from "react";
import {
  getTenantById,
  getTenantManagement,
} from "../../../../../shared/services/apis";
import { FaEnvelope, FaTrophy } from "react-icons/fa";
import saveAs from "file-saver";
import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
import { Paragraph } from "docx";
import { RiDownloadLine } from "react-icons/ri";

interface ManagementProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

interface ManagementData {
  leadership: any[];
  boardMembers: any[];
  keyPersonnel: any[];
  executives: Executive[];
}

interface Executive {
  id: string;
  name: string;
  title: string;
  image: string;
  tenure: string;
  linkedin_url: string;
  email?: string;
  timeline: {
    title: string;
    company: string;
    description: string;
    dateRange: string;
  }[];
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

export const Management: React.FC<ManagementProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<ManagementData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedExec, setSelectedExec] = useState<any>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false);
  const [downloadingWord, setDownloadingWord] = useState<boolean>(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchManagementData = async () => {
      try {
        const response = await getTenantManagement(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );

        // Transform data to match executive format
        const executives =
          response.map((leader: any) => {
            const earliestStartDate = leader.employmentHistory?.reduce(
              (earliest: Date | null, job: any) => {
                if (!job.startDate) return earliest;
                const startDate = new Date(job.startDate);
                return !earliest || startDate < earliest ? startDate : earliest;
              },
              null
            );

            const formattedYear = earliestStartDate
              ? earliestStartDate.getFullYear()
              : "N/A";

            // Synthetic unique ID
            const id = `${leader.name}-${leader.currentCompany}`
              .replace(/\s+/g, "-")
              .toLowerCase();

            // Filter locked email
            const email =
              leader.emails?.[0]?.includes("email_not_unlocked") ||
              !leader.emails?.[0]
                ? undefined
                : leader.emails[0];

            return {
              id,
              name: leader.name,
              title:
                leader.currentRole ||
                leader.title ||
                leader.role ||
                "Executive",
              image:
                leader.imageUrl ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  leader.name
                )}&background=3b82f6&color=ffffff`,
              tenure: earliestStartDate ? `${formattedYear} - Present` : "N/A",
              linkedin_url: leader.linkedin,
              email,
              timeline:
                leader.employmentHistory?.map((job: any) => {
                  const dateRange = job.startDate
                    ? `${new Date(job.startDate).toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                      })} - ${
                        job.endDate
                          ? new Date(job.endDate).toLocaleString("default", {
                              month: "short",
                              year: "numeric",
                            })
                          : "Present"
                      }`
                    : "Date not available";

                  return {
                    title: job.title || "Position Title",
                    company: job.company || "Company not specified",
                    description:
                      job.description ||
                      job.diescription ||
                      "No description available.",
                    dateRange,
                  };
                }) || [],
            };
          }) || [];

        setData({ ...response, executives });
        if (executives.length > 0) {
          setSelectedExec(executives[0]);
        }
      } catch (error) {
        console.error("Error fetching management data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchManagementData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      let currentY = 20;

      // Load the watermark image
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 }));

        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        doc.restoreGraphicsState();
      };

      // Add title
      doc.setFontSize(22);
      doc.setTextColor(0, 51, 102);
      doc.text("Management Team", pageWidth / 2, currentY, { align: "center" });
      currentY += 15;

      const { autoTable } = await import("jspdf-autotable");

      // Add each executive's details
      if (data?.executives && data.executives.length > 0) {
        for (let i = 0; i < data.executives.length; i++) {
          const exec = data.executives[i];

          // Check if we need a new page
          if (currentY > pageHeight - 60) {
            doc.addPage();
            currentY = margin;
          }

          // Add executive name
          doc.setFontSize(16);
          doc.setTextColor(0, 0, 0);
          doc.text(exec.name, margin, currentY);
          currentY += 8;

          // Add title and tenure
          doc.setFontSize(12);
          doc.setTextColor(68, 68, 68);
          doc.text(`${exec.title} • ${exec.tenure}`, margin, currentY);
          currentY += 12;

          // Add timeline table
          const timelineData = exec.timeline.map((entry: any) => [
            entry.dateRange,
            entry.title,
            entry.company,
            entry.description,
          ]);

          autoTable(doc, {
            startY: currentY,
            head: [["Period", "Position", "Company", "Details"]],
            body: timelineData,
            theme: "striped",
            headStyles: {
              fillColor: [0, 51, 102],
              textColor: [255, 255, 255],
              fontStyle: "bold",
            },
            bodyStyles: {
              textColor: [0, 0, 0],
            },
            margin: { left: margin, right: margin },
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 40 },
              2: { cellWidth: 35 },
              3: { cellWidth: 60 },
            },
          });

          currentY = (doc as any).lastAutoTable.finalY + 15;

          // Add separator between executives
          if (i < data.executives.length - 1) {
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, currentY, pageWidth - margin, currentY);
            currentY += 10;
          }
        }
      }

      // Add footer to all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      doc.save(`Management_Team_${new Date().toISOString().slice(0, 10)}.pdf`);
      console.log("PDF generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(
        `Failed to generate PDF: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloadingPDF(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      const {
        Document,
        Paragraph,
        Packer,
        HeadingLevel,
        AlignmentType,
        TextRun,
        ImageRun,
        Footer,
      } = await import("docx");

      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      // Create content for all executives
      const executiveContent: Paragraph[] = [
        new Paragraph({
          text: "Management Team",
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        }),
        new Paragraph({
          text: `Generated on ${new Date().toLocaleDateString()}`,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),
      ];

      if (data?.executives && data.executives.length > 0) {
        data.executives.forEach((exec, index) => {
          executiveContent.push(
            new Paragraph({
              text: exec.name,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 50 },
            }),
            new Paragraph({
              text: `${exec.title} • ${exec.tenure}`,
              spacing: { after: 150 },
            }),
            new Paragraph({
              text: "Career Timeline",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 100, after: 100 },
            }),
            ...exec.timeline.map(
              (entry: any) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${entry.dateRange} - `,
                      bold: true,
                    }),
                    new TextRun({
                      text: `${entry.title} at ${entry.company}`,
                    }),
                    new TextRun({
                      text: `\n${entry.description}`,
                      italics: true,
                      color: "666666",
                    }),
                  ],
                  spacing: { after: 100 },
                })
            )
          );

          // Add page break between executives (except for the last one)
          if (index < data.executives.length - 1) {
            executiveContent.push(
              new Paragraph({
                text: "",
                pageBreakBefore: true,
              })
            );
          }
        });
      }

      // Create footer with logo
      const footerChildren: Paragraph[] = [
        new Paragraph({
          children: [
            new TextRun({
              text: `Confidential - Generated by ${
                tenant?.name || "Management System"
              }`,
              size: 10,
              color: "888888",
            }),
          ],
        }),
      ];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      const doc = new Document({
        creator: "Management System",
        title: "Management Team",
        description: "Complete management team report",
        sections: [
          {
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: executiveContent,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `Management_Team_${new Date()
        .toISOString()
        .slice(0, 10)}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="m-auto w-full max-w-[70vw] md:max-w-4xl rounded-2xl bg-white shadow-xl mt-20 animate-pulse">
        {/* Skeleton Executive Carousel */}
        <div className="flex overflow-x-auto gap-6 px-4 py-4 bg-[#F8FAFC] rounded-t-xl">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center px-4 py-4 min-w-[120px] max-w-[200px] max-h-[200px]"
            >
              <div className="rounded-full bg-gray-200 w-16 h-16 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-20 mb-1" />
            </div>
          ))}
        </div>

        {/* Skeleton Executive Details */}
        <div className="py-6 px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 w-40 rounded" />
              <div className="h-4 bg-gray-100 w-32 rounded" />
            </div>
            <div className="h-8 w-32 bg-gray-200 rounded-md" />
          </div>

          {/* Skeleton Timeline */}
          <div className="relative w-full py-4 mt-10">
            <div className="flex gap-12 overflow-x-auto min-w-max">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center max-w-[200px]"
                >
                  <div className="h-4 bg-gray-200 w-28 rounded mb-2" />
                  <div className="bg-gray-300 w-3 h-3 rounded-full mb-4" />
                  <div className="h-4 bg-gray-200 w-32 rounded mb-1" />
                  <div className="h-3 bg-gray-100 w-28 rounded" />
                </div>
              ))}
            </div>
          </div>

          {/* Skeleton Highlight Banner */}
          <div className="flex justify-between py-4 border-t border-gray-200 gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gray-200 w-5 h-5 rounded-full" />
              <div className="h-4 bg-gray-100 w-56 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="m-auto max-w-4xl max-h-[70vh] overflow-y-auto rounded-2xl bg-white mt-20 shadow-lg">
        <div className="text-center py-8 text-red-500">
          <p>Failed to load management data.</p>
        </div>
      </div>
    );
  }

  const { executives } = data;

  return (
    <div className="m-auto w-full  max-w-4xl rounded-2xl bg-white shadow-xl mt-0 2xl:mt-20 ">
      {/* Executive Carousel */}
      <div className="flex gap-3 my-2 justify-end">
        <button
          onClick={downloadPDF}
          disabled={downloadingPDF}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50 disabled:opacity-50"
        >
          <RiDownloadLine className="w-4 h-4" />
          {downloadingPDF ? "Generating..." : "PDF"}
        </button>
        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50 disabled:opacity-50"
        >
          <RiDownloadLine className="w-4 h-4" />
          {downloadingWord ? "Generating..." : "Word"}
        </button>
      </div>

      {executives && executives.length > 0 && (
        <div className="flex flex-grow overflow-x-auto gap-6 px-4 py-2 bg-[#F8FAFC] shadow-inner rounded-t-xl  ">
          {executives.map((exec) => (
            <div
              key={exec.id}
              className={`flex flex-col items-center px-4 py-4 min-w-[120px] max-w-[200px] max-h-[200px]  cursor-pointer transition-transform hover:bg-white/60 hover:scale-105 rounded-2xl `}
              onClick={() => setSelectedExec(exec)}
            >
              <div className="relative mb-2">
                <img
                  src={exec.image}
                  onError={(e) => {
                    const fallback = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                      exec.name
                    )}`;
                    e.currentTarget.src = fallback;
                  }}
                  className={`rounded-full object-cover transition-all duration-300 ${
                    selectedExec?.id === exec.id ? "w-20 h-20" : "w-16 h-16 "
                  }`}
                />
              </div>
              <p className="text-center text-sm font-medium text-gray-800 leading-tight">
                {exec.name}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Executive Details */}
      {selectedExec && (
        <div className="py-6 bg-white rounded-b-xl ">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center px-6 gap-4">
            <div>
              <h2 className="text-2xl  text-gray-600">{selectedExec.name}</h2>
              <p className="text-gray-600 mt-1 max-w-[30vw] font-light">
                {selectedExec.title} • {selectedExec.tenure}
              </p>
            </div>
            <div className="flex gap-3">
              {selectedExec.linkedin_url && (
                <a
                  href={selectedExec.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2  text-blue-800  hover:text-black transition-colors cursor-pointer"
                >
                  <FaEnvelope className="w-4 h-4" /> LinkedIn ↗
                </a>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="px-8 py-4 mt-10">
            <div className="relative">
              <div className="overflow-x-auto">
                <div className="relative flex items-start min-w-max pb-6">
                  <div
                    className="absolute border-t-2 border-dashed border-gray-200 z-0"
                    style={{
                      top: "34px",
                      left: "0px",
                      right: "0px",
                      width: "calc(100% - 190px)",
                    }}
                  />

                  {/* Timeline Items */}
                  <div className="flex gap-8 sm:gap-12 relative z-10">
                    {selectedExec.timeline?.map((entry: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex flex-col items-start min-w-[160px] max-w-[200px]"
                      >
                        <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2 text-center whitespace-nowrap">
                          {entry.dateRange}
                        </p>

                        <div
                          className={`rounded-full flex-shrink-0  ml-2 ${
                            idx === 0
                              ? "bg-white w-3 h-3 sm:w-4 sm:h-4 border-2 border-gray-400"
                              : "bg-gray-300 w-2.5 h-2.5 sm:w-3 sm:h-3"
                          }`}
                        />

                        <div className="mt-3  sm:mt-4 text-left">
                          <p className="text-xs sm:text-sm text-gray-800  leading-tight">
                            {entry.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 sm:mt-2 leading-tight">
                            {entry.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Highlight Banner */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between py-4 border-t border-gray-200 px-6 gap-4">
            <div className="flex items-center">
              <FaTrophy className="text-blue-500 w-5 h-5 mr-3" />
              <span className="font-medium text-gray-800">
                Exceptional contribution to business growth
              </span>
            </div>
            {/* <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                WSJ
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                Financial Times
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                Morning Star
              </span>
            </div> */}
          </div>
        </div>
      )}

      {/* Additional Sections */}
      <div className="">
        {/* Empty State */}
        {/* {(!leadership || leadership.length === 0) &&
          (!boardMembers || boardMembers.length === 0) &&
          (!keyPersonnel || keyPersonnel.length === 0) && (
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <p className="text-lg">No management information available</p>
              <p className="text-sm mt-2">
                Management data will be populated once available from data
                sources
              </p>
            </div>
          )} */}
      </div>
    </div>
  );
};
